import React, { useState } from 'react';
import {
  FileCheck,
  Send,
  ShieldCheck,
  ShieldAlert,
  CheckCircle2,
  ArrowRight,
  Briefcase
} from 'lucide-react';
import { EditorialProject } from '../../../../../types/EditorialProject.types';

const cn = (...args: any[]) => args.filter(Boolean).join(' ');

interface Phase2Props {
  project: EditorialProject;
  onUpdateProject: (updated: EditorialProject) => void;
  onAdvanceToPhase3: (project: EditorialProject) => void;
}

export default function EditorialPhase2Contracts({
  project,
  onUpdateProject,
  onAdvanceToPhase3
}: Phase2Props) {
  const [advanceInput, setAdvanceInput] = useState(project.contractDetails?.advanceAmount || 3000);
  const [durationYears, setDurationYears] = useState<5 | 10>(project.contractDetails?.durationYears || 10);
  const [territoryInput, setTerritoryInput] = useState(project.contractDetails?.territory || 'España y Latinoamérica');
  const [selectedFormats, setSelectedFormats] = useState<string[]>(project.contractDetails?.authorizedFormats || ['Tapa Dura', 'Rústica', 'E-book']);
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>(project.contractDetails?.authorizedLanguages || ['Castellano']);

  const [tier1Rate, setTier1Rate] = useState(project.contractDetails?.royaltyTiers[0]?.rate || 8);
  const [tier1UpTo, setTier1UpTo] = useState(project.contractDetails?.royaltyTiers[0]?.upToQty || 2000);
  const [tier2Rate, setTier2Rate] = useState(project.contractDetails?.royaltyTiers[1]?.rate || 10);
  const [tier2UpTo, setTier2UpTo] = useState(project.contractDetails?.royaltyTiers[1]?.upToQty || 5000);
  const [tier3Rate, setTier3Rate] = useState(project.contractDetails?.royaltyTiers[2]?.rate || 12);

  const contractStatus = project.contractDetails?.status || 'draft';
  const isContractSigned = contractStatus === 'signed';

  // Regla 2: no se puede pasar a producción sin contrato firmado
  const canAdvanceToProduction = isContractSigned;

  const handleSendToDocuSign = () => {
    onUpdateProject({
      ...project,
      contractDetails: {
        status: 'docusign_sent',
        docusignEnvelopeId: `DOCUSIGN-ENV-${Math.floor(10000 + Math.random() * 90000)}-${project.bookAuthor.substring(0, 4).toUpperCase()}`,
        signedDate: undefined,
        advanceAmount: advanceInput,
        advanceAmortized: 0,
        advanceTreasuryPaid: false,
        royaltyTiers: [{ upToQty: tier1UpTo, rate: tier1Rate }, { upToQty: tier2UpTo, rate: tier2Rate }, { upToQty: 999999, rate: tier3Rate }],
        territory: territoryInput,
        authorizedLanguages: selectedLanguages,
        authorizedFormats: selectedFormats,
        durationYears,
        exclusivity: project.contractDetails?.exclusivity ?? true
      }
    });
  };

  const handleSimulateAuthorSignature = () => {
    onUpdateProject({
      ...project,
      contractDetails: {
        status: 'signed',
        docusignEnvelopeId: project.contractDetails?.docusignEnvelopeId || `DOCUSIGN-ENV-OK-${Date.now().toString().slice(-6)}`,
        signedDate: new Date().toISOString().split('T')[0],
        advanceAmount: advanceInput,
        advanceAmortized: 0,
        advanceTreasuryPaid: advanceInput > 0,
        royaltyTiers: [{ upToQty: tier1UpTo, rate: tier1Rate }, { upToQty: tier2UpTo, rate: tier2Rate }, { upToQty: 999999, rate: tier3Rate }],
        territory: territoryInput,
        authorizedLanguages: selectedLanguages,
        authorizedFormats: selectedFormats,
        durationYears,
        exclusivity: project.contractDetails?.exclusivity ?? true
      }
    });
  };

  const handleProceedToProduction = () => {
    if (!canAdvanceToProduction) return;
    const updated: EditorialProject = { ...project, editorialPhase: 'produccion', status: 'editing' };
    onUpdateProject(updated);
    onAdvanceToPhase3(updated);
  };

  return (
    <div className="ed-panel">
      <div className="ed-panel-header">
        <h3 className="ed-panel-title">
          <FileCheck size={14} style={{ color: 'var(--color-brand-green)' }} />
          Contratación_De_Derechos_De_Autor (DocuSign)
        </h3>
        <span className={cn('ed-badge', contractStatus === 'signed' ? 'ed-badge--green' : contractStatus === 'docusign_sent' ? 'ed-badge--sky' : 'ed-badge--amber')}>
          DocuSign: {contractStatus === 'signed' ? 'FIRMADO' : contractStatus === 'docusign_sent' ? 'ENVIADO A FIRMA' : 'BORRADOR'}
        </span>
      </div>

      <div className="ed-col-stack">
        {/* Sobre DocuSign */}
        <div className="ed-box">
          <div className="ed-row-between" style={{ marginBottom: '0.5rem' }}>
            <span style={{ fontSize: '0.5625rem', fontWeight: 700, color: '#fff', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
              <CheckCircle2 size={13} style={{ color: isContractSigned ? 'var(--color-brand-green)' : '#64748b' }} />
              Sobre DocuSign Oficial
            </span>
            <span style={{ fontSize: '0.5rem', color: '#94a3b8' }}>ID: {project.contractDetails?.docusignEnvelopeId || 'PENDIENTE DE EMISIÓN'}</span>
          </div>

          {isContractSigned ? (
            <div className="ed-alert ed-alert--success" style={{ justifyContent: 'space-between' }}>
              <span>Firmado digitalmente por ambas partes el {project.contractDetails?.signedDate}.</span>
              <span className="ed-badge ed-badge--green">VERIFICADO DOCUSIGN</span>
            </div>
          ) : contractStatus === 'docusign_sent' ? (
            <div className="ed-alert ed-alert--info" style={{ justifyContent: 'space-between' }}>
              <span>Enviado al correo del autor / agencia. Esperando firma biométrica DocuSign.</span>
              <button onClick={handleSimulateAuthorSignature} className="ed-btn ed-btn--sky">Simular Firma de Autor</button>
            </div>
          ) : (
            <div className="ed-alert ed-alert--warning" style={{ justifyContent: 'space-between' }}>
              <span>Borrador parametrizado listo para emitir sobre DocuSign.</span>
              <button onClick={handleSendToDocuSign} className="ed-btn ed-btn--primary"><Send size={11} /> Emitir Sobre DocuSign</button>
            </div>
          )}
        </div>

        <div className="ed-grid-2">
          <div className="ed-box">
            <span className="ed-label">Anticipo de Derechos (Advance)</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input type="number" step={500} value={advanceInput} onChange={(e) => setAdvanceInput(Number(e.target.value))} className="ed-input" />
              <span className="ed-value">€</span>
            </div>
            <span style={{ fontSize: '0.5rem', color: '#94a3b8', display: 'block', marginTop: '0.25rem' }}>A compensar íntegramente con futuras ventas en la liquidación.</span>
          </div>

          <div className="ed-box">
            <span className="ed-label">Periodo de Vigencia del Contrato</span>
            <div className="ed-btn-row-2" style={{ marginTop: '0.25rem' }}>
              {[5, 10].map((years) => (
                <button key={years} type="button" onClick={() => setDurationYears(years as 5 | 10)} className={cn('ed-badge', durationYears === years && 'ed-badge--green')} style={{ padding: '0.375rem', textAlign: 'center' }}>
                  {years} AÑOS
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="ed-box">
          <span className="ed-label" style={{ marginBottom: '0.5rem', fontWeight: 700 }}>Escalado de Royalties (% sobre PVP según volumen de ventas)</span>
          <div className="ed-grid-3">
            <div className="ed-box" style={{ background: 'var(--color-brand-blue-light)' }}>
              <span style={{ fontSize: '0.5rem', color: '#94a3b8', display: 'block' }}>Tramo 1 (hasta {tier1UpTo.toLocaleString()} ej.)</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', marginTop: '0.25rem' }}>
                <input type="number" value={tier1Rate} onChange={(e) => setTier1Rate(Number(e.target.value))} className="ed-input" style={{ width: '3rem', textAlign: 'center', padding: '0.25rem' }} />
                <span className="ed-value">%</span>
              </div>
            </div>
            <div className="ed-box" style={{ background: 'var(--color-brand-blue-light)' }}>
              <span style={{ fontSize: '0.5rem', color: '#94a3b8', display: 'block' }}>Tramo 2 (hasta {tier2UpTo.toLocaleString()} ej.)</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', marginTop: '0.25rem' }}>
                <input type="number" value={tier2Rate} onChange={(e) => setTier2Rate(Number(e.target.value))} className="ed-input" style={{ width: '3rem', textAlign: 'center', padding: '0.25rem' }} />
                <span className="ed-value">%</span>
              </div>
            </div>
            <div className="ed-box" style={{ background: 'var(--color-brand-blue-light)' }}>
              <span style={{ fontSize: '0.5rem', color: '#94a3b8', display: 'block' }}>Tramo 3 (&gt; {tier2UpTo.toLocaleString()} ej.)</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', marginTop: '0.25rem' }}>
                <input type="number" value={tier3Rate} onChange={(e) => setTier3Rate(Number(e.target.value))} className="ed-input" style={{ width: '3rem', textAlign: 'center', padding: '0.25rem' }} />
                <span className="ed-value">%</span>
              </div>
            </div>
          </div>
        </div>

        <div className="ed-grid-3">
          <div className="ed-box">
            <span className="ed-label" style={{ marginBottom: '0.25rem' }}>Ámbito Territorial</span>
            <input type="text" value={territoryInput} onChange={(e) => setTerritoryInput(e.target.value)} className="ed-input" />
          </div>
          <div className="ed-box">
            <span className="ed-label" style={{ marginBottom: '0.25rem' }}>Formatos Autorizados</span>
            <div className="ed-col-stack" style={{ gap: '0.25rem' }}>
              {['Tapa Dura', 'Rústica', 'E-book', 'Audiolibro'].map((fmt) => (
                <label key={fmt} className="ed-checkbox-row">
                  <input
                    type="checkbox"
                    checked={selectedFormats.includes(fmt)}
                    onChange={(e) => setSelectedFormats(e.target.checked ? [...selectedFormats, fmt] : selectedFormats.filter(f => f !== fmt))}
                  />
                  {fmt}
                </label>
              ))}
            </div>
          </div>
          <div className="ed-box">
            <span className="ed-label" style={{ marginBottom: '0.25rem' }}>Idiomas Autorizados</span>
            <div className="ed-col-stack" style={{ gap: '0.25rem' }}>
              {['Castellano', 'Catalán', 'Gallego', 'Euskera', 'Traducciones'].map((lang) => (
                <label key={lang} className="ed-checkbox-row">
                  <input
                    type="checkbox"
                    checked={selectedLanguages.includes(lang)}
                    onChange={(e) => setSelectedLanguages(e.target.checked ? [...selectedLanguages, lang] : selectedLanguages.filter(l => l !== lang))}
                  />
                  {lang}
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="ed-connector">
          <span className="ed-connector-title"><Briefcase size={12} style={{ color: '#38bdf8' }} /> Puntos de Conexión Activos</span>
          <div className="ed-row-between" style={{ borderBottom: '1px solid #1e293b', paddingBottom: '0.375rem' }}>
            <span style={{ color: '#94a3b8' }}>Con Financiero / Tesorería:</span>
            <span style={{ fontWeight: 700, textTransform: 'uppercase', color: isContractSigned ? 'var(--color-brand-green)' : '#fbbf24' }}>
              {isContractSigned ? `Orden de pago de anticipo (${advanceInput.toLocaleString()}€) emitida a Tesorería` : 'Orden de pago en espera de firma DocuSign'}
            </span>
          </div>
          {project.modality === 'encargo' && (
            <div className="ed-row-between">
              <span style={{ color: '#94a3b8' }}>Con CRM / Ventas (Por Encargo):</span>
              <span style={{ color: '#c4b5fd', fontWeight: 700, textTransform: 'uppercase' }}>Oferta vinculante enviada al cliente {project.clientName || 'Cliente'}</span>
            </div>
          )}
        </div>

        {!canAdvanceToProduction ? (
          <div className="ed-alert ed-alert--danger">
            <ShieldAlert size={16} />
            <div><strong>Control de Exclusividad y Bloqueo de Fase 2:</strong> El sistema impide pasar la obra a producción si no existe un contrato adjunto en estado <strong>Firmado</strong>. Emita o simule la firma DocuSign para desbloquear la Fase 3.</div>
          </div>
        ) : (
          <div className="ed-alert ed-alert--success">
            <ShieldCheck size={16} />
            <span>Contrato validado. Se autoriza el desbloqueo de la Ficha Técnica, el bloque ISBN y la Producción Editorial.</span>
          </div>
        )}

        <button type="button" disabled={!canAdvanceToProduction} onClick={handleProceedToProduction} className="ed-btn ed-btn--primary ed-btn--block">
          <ArrowRight size={14} /> Avanzar a Fase 3: Producción Editorial &amp; Cronograma
        </button>
      </div>
    </div>
  );
}
